<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import CannedResponseAttachments from './CannedResponseAttachments.vue';
import CannedButtonFields from './CannedButtonFields.vue';
import CannedResponsePreview from './CannedResponsePreview.vue';

// Shared add/edit form: three colour-coded chapters (identity, message,
// interactive button) next to a live "what the customer receives" preview.
const props = defineProps({
  mode: { type: String, default: 'add' }, // add | edit
  initial: { type: Object, default: () => ({}) },
  excludeId: { type: Number, default: null },
  isLoading: { type: Boolean, default: false },
});
const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();
const store = useStore();

const MAX_BODY_WITH_BUTTON = 640;
const INPUT_CLASS =
  'w-full rounded-lg border border-n-weak bg-n-alpha-black1 px-2.5 py-1.5 text-[13px] font-semibold text-n-slate-12 outline-none transition-colors placeholder:font-normal placeholder:text-n-slate-9 focus:border-n-brand';

const shortCode = ref(props.initial.shortCode || '');
const content = ref(props.initial.content || '');
const category = ref(props.initial.category || '');
const attachments = ref([...(props.initial.attachments || [])]);
const buttonLabel = ref(props.initial.buttonLabel || '');
const buttonReplyContent = ref(props.initial.buttonReplyContent || '');
const buttonAttachments = ref([...(props.initial.buttonAttachments || [])]);
const buttonEnabled = ref(
  Boolean(
    props.initial.buttonLabel ||
      props.initial.buttonReplyContent ||
      (props.initial.buttonAttachments || []).length
  )
);
const buttonError = ref(null);
const touched = ref({ shortCode: false, content: false });

// Switching the button off discards its fields so a stale label can never be
// saved behind a closed section.
watch(buttonEnabled, enabled => {
  if (enabled) return;
  buttonLabel.value = '';
  buttonReplyContent.value = '';
  buttonAttachments.value = [];
  // The fields component is unmounted by v-if, so it never emits a final
  // null — clear its last error here or Save stays disabled.
  buttonError.value = null;
});

const existingCategories = computed(() => {
  const records = store.getters.getCannedResponses || [];
  return [...new Set(records.map(r => r.category).filter(Boolean))].sort();
});

const shortCodeError = computed(() => {
  if (!touched.value.shortCode) return null;
  if (shortCode.value.trim().length < 2)
    return t('CANNED_MGMT.FORM.SHORT_CODE_ERROR');
  return null;
});
const contentError = computed(() => {
  if (!touched.value.content) return null;
  if (!content.value.trim()) return t('CANNED_MGMT.FORM.CONTENT_ERROR');
  return null;
});
const contentOverCap = computed(
  () => buttonEnabled.value && content.value.length > MAX_BODY_WITH_BUTTON
);
const canSubmit = computed(
  () =>
    shortCode.value.trim().length >= 2 &&
    Boolean(content.value.trim()) &&
    !buttonError.value &&
    !props.isLoading
);

const chapters = computed(() => [
  {
    key: 'identity',
    number: '١',
    badge: 'bg-n-brand/10 text-n-brand',
    icon: 'i-lucide-tag',
    title: t('CANNED_MGMT.FORM.CHAPTER_IDENTITY'),
    hint: t('CANNED_MGMT.FORM.CHAPTER_IDENTITY_HINT'),
  },
  {
    key: 'message',
    number: '٢',
    badge: 'bg-n-blue-3 text-n-blue-11',
    icon: 'i-lucide-message-square-text',
    title: t('CANNED_MGMT.FORM.CHAPTER_MESSAGE'),
    hint: t('CANNED_MGMT.FORM.CHAPTER_MESSAGE_HINT'),
  },
  {
    key: 'button',
    number: '٣',
    badge: 'bg-n-teal-3 text-n-teal-11',
    icon: 'i-lucide-mouse-pointer-click',
    title: t('CANNED_MGMT.FORM.CHAPTER_BUTTON'),
    hint: t('CANNED_MGMT.FORM.CHAPTER_BUTTON_HINT'),
  },
]);
const chapter = key => chapters.value.find(c => c.key === key);

const submit = () => {
  touched.value = { shortCode: true, content: true };
  if (!canSubmit.value) return;
  emit('submit', {
    shortCode: shortCode.value.trim(),
    content: content.value,
    category: category.value.trim() || null,
    attachments: attachments.value,
    buttonLabel: buttonEnabled.value ? buttonLabel.value.trim() : '',
    buttonReplyContent: buttonEnabled.value ? buttonReplyContent.value : '',
    buttonAttachments: buttonEnabled.value ? buttonAttachments.value : [],
  });
};
</script>

<template>
  <form
    class="canned-form flex w-full flex-col !p-0 !self-stretch"
    @submit.prevent="submit"
  >
    <!-- header -->
    <div class="border-b border-n-weak px-5 pb-3 pt-4">
      <div
        class="mb-0.5 text-[10.5px] font-extrabold uppercase tracking-wide text-n-brand"
      >
        {{ $t('CANNED_MGMT.FORM.EYEBROW') }}
      </div>
      <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 class="text-[17px] font-extrabold text-n-slate-12">
          {{
            mode === 'edit'
              ? $t('CANNED_MGMT.FORM.TITLE_EDIT')
              : $t('CANNED_MGMT.FORM.TITLE_ADD')
          }}
        </h2>
        <span
          v-if="mode === 'edit' && initial.shortCode"
          class="rounded-md bg-n-slate-3 px-1.5 py-0.5 text-[12.5px] font-bold text-n-slate-11"
        >
          /{{ initial.shortCode }}
        </span>
      </div>
      <p class="mt-0.5 text-[11.5px] font-medium text-n-slate-10">
        {{ $t('CANNED_MGMT.FORM.SUBTITLE') }}
      </p>
    </div>

    <div class="grid gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_16.5rem]">
      <!-- chapters -->
      <div class="flex min-w-0 flex-col gap-3">
        <!-- ١ identity -->
        <section class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <header class="mb-3 flex items-start gap-2.5">
            <span
              class="grid size-7 shrink-0 place-items-center rounded-lg"
              :class="chapter('identity').badge"
            >
              <span :class="chapter('identity').icon" class="size-4" />
            </span>
            <div>
              <h3 class="text-[13.5px] font-extrabold text-n-slate-12">
                {{ chapter('identity').title }}
              </h3>
              <p class="mt-0.5 text-[11px] font-medium text-n-slate-10">
                {{ chapter('identity').hint }}
              </p>
            </div>
          </header>
          <div class="grid gap-3 sm:grid-cols-2">
            <div>
              <label
                class="mb-1 block text-[12px] font-bold"
                :class="shortCodeError ? 'text-n-ruby-11' : 'text-n-slate-12'"
              >
                {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
              </label>
              <div class="relative">
                <span
                  class="pointer-events-none absolute inset-y-0 start-2.5 flex items-center text-[13px] font-extrabold text-n-slate-9"
                >
                  /
                </span>
                <input
                  v-model="shortCode"
                  type="text"
                  :class="[
                    INPUT_CLASS,
                    'ps-6',
                    shortCodeError && '!border-n-ruby-9',
                  ]"
                  :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
                  @blur="touched.shortCode = true"
                />
              </div>
              <p
                class="mt-1 text-[11px] font-medium"
                :class="shortCodeError ? 'text-n-ruby-11' : 'text-n-slate-10'"
              >
                {{ shortCodeError || $t('CANNED_MGMT.FORM.SHORT_CODE_HINT') }}
              </p>
            </div>
            <div>
              <label class="mb-1 block text-[12px] font-bold text-n-slate-12">
                {{ $t('CANNED_MGMT.ADD.FORM.CATEGORY.LABEL') }}
              </label>
              <input
                v-model="category"
                type="text"
                :class="INPUT_CLASS"
                :list="`canned-category-options-${mode}`"
                :placeholder="$t('CANNED_MGMT.ADD.FORM.CATEGORY.PLACEHOLDER')"
              />
              <datalist :id="`canned-category-options-${mode}`">
                <option
                  v-for="cat in existingCategories"
                  :key="cat"
                  :value="cat"
                />
              </datalist>
              <p class="mt-1 text-[11px] font-medium text-n-slate-10">
                {{ $t('CANNED_MGMT.FORM.CATEGORY_HINT') }}
              </p>
            </div>
          </div>
        </section>

        <!-- ٢ message -->
        <section class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <header class="mb-3 flex items-start gap-2.5">
            <span
              class="grid size-7 shrink-0 place-items-center rounded-lg"
              :class="chapter('message').badge"
            >
              <span :class="chapter('message').icon" class="size-4" />
            </span>
            <div class="min-w-0 flex-1">
              <h3 class="text-[13.5px] font-extrabold text-n-slate-12">
                {{ chapter('message').title }}
              </h3>
              <p class="mt-0.5 text-[11px] font-medium text-n-slate-10">
                {{ chapter('message').hint }}
              </p>
            </div>
            <span
              class="shrink-0 rounded-md px-1.5 py-0.5 text-[10.5px] font-bold tabular-nums"
              :class="
                contentOverCap
                  ? 'bg-n-amber-3 text-n-amber-11'
                  : 'bg-n-slate-3 text-n-slate-10'
              "
              dir="ltr"
              :title="$t('CANNED_MGMT.FORM.CHARS_LIMIT_HINT')"
            >
              {{ content.length }}
              <template v-if="buttonEnabled">/ {{ MAX_BODY_WITH_BUTTON }}</template>
            </span>
          </header>

          <div
            class="overflow-hidden rounded-lg border transition-colors focus-within:border-n-brand"
            :class="contentError ? 'border-n-ruby-9' : 'border-n-weak'"
          >
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-3"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @blur="touched.content = true"
            />
          </div>
          <p
            v-if="contentError"
            class="mt-1 text-[11px] font-bold text-n-ruby-11"
          >
            {{ contentError }}
          </p>

          <div class="mt-3 border-t border-n-weak/70 pt-3">
            <label
              class="mb-1.5 flex items-center gap-1.5 text-[12px] font-bold text-n-slate-12"
            >
              <span class="i-lucide-paperclip size-3.5 text-n-slate-10" />
              {{ $t('CANNED_MGMT.ATTACHMENTS.LABEL') }}
            </label>
            <CannedResponseAttachments v-model="attachments" />
          </div>
        </section>

        <!-- ٣ interactive button -->
        <section
          class="rounded-xl border bg-n-solid-1 p-4 transition-colors"
          :class="buttonEnabled ? 'border-n-teal-7' : 'border-n-weak'"
        >
          <header class="flex items-start gap-3">
            <span
              class="grid size-7 shrink-0 place-items-center rounded-lg"
              :class="chapter('button').badge"
            >
              <span :class="chapter('button').icon" class="size-4" />
            </span>
            <div class="min-w-0 flex-1">
              <h3 class="text-[13.5px] font-extrabold text-n-slate-12">
                {{ chapter('button').title }}
                <span
                  class="ms-1.5 align-middle rounded-md bg-n-teal-3 px-1.5 py-0.5 text-[10px] font-extrabold text-n-teal-11"
                >
                  {{ $t('CANNED_MGMT.FORM.OPTIONAL') }}
                </span>
              </h3>
              <p class="mt-0.5 text-[11px] font-medium leading-relaxed text-n-slate-10">
                {{ chapter('button').hint }}
              </p>
            </div>
            <label class="flex shrink-0 cursor-pointer items-center gap-2">
              <span class="text-[11.5px] font-bold text-n-slate-11">
                {{ $t('CANNED_MGMT.FORM.BUTTON_SWITCH') }}
              </span>
              <Switch v-model="buttonEnabled" />
            </label>
          </header>

          <div v-if="buttonEnabled" class="mt-3 border-t border-n-teal-6/40 pt-3">
            <CannedButtonFields
              v-model:label="buttonLabel"
              v-model:reply-content="buttonReplyContent"
              v-model:attachments="buttonAttachments"
              :content="content"
              :exclude-id="excludeId"
              @update:error="buttonError = $event"
            />
          </div>
        </section>
      </div>

      <!-- live preview -->
      <aside class="lg:sticky lg:top-0 lg:self-start">
        <div class="mb-1.5 flex items-center gap-1.5 text-[11.5px] font-extrabold text-n-slate-11">
          <span class="i-lucide-smartphone size-3.5" />
          {{ $t('CANNED_MGMT.PREVIEW.TITLE') }}
        </div>
        <CannedResponsePreview
          :content="content"
          :attachments="attachments"
          :button-label="buttonEnabled ? buttonLabel : ''"
          :button-reply-content="buttonReplyContent"
          :button-attachments="buttonAttachments"
        />
        <p class="mt-1.5 text-[10.5px] font-medium leading-relaxed text-n-slate-10">
          {{ $t('CANNED_MGMT.PREVIEW.NOTE') }}
        </p>
      </aside>
    </div>

    <!-- footer -->
    <div
      class="sticky bottom-0 flex items-center justify-between gap-3 border-t border-n-weak bg-n-solid-1/95 px-5 py-2 backdrop-blur"
    >
      <p
        class="min-w-0 truncate text-[11.5px] font-bold"
        :class="buttonError || shortCodeError || contentError ? 'text-n-ruby-11' : 'text-n-slate-10'"
      >
        {{
          buttonError ||
          shortCodeError ||
          contentError ||
          $t('CANNED_MGMT.FORM.READY_HINT')
        }}
      </p>
      <div class="flex shrink-0 items-center gap-2">
        <NextButton
          faded
          slate
          type="button"
          :label="$t('CANNED_MGMT.FORM.CANCEL')"
          @click="emit('cancel')"
        />
        <NextButton
          type="submit"
          icon="i-lucide-check"
          :label="
            mode === 'edit'
              ? $t('CANNED_MGMT.FORM.SAVE_EDIT')
              : $t('CANNED_MGMT.FORM.SAVE_ADD')
          "
          :disabled="!canSubmit"
          :is-loading="isLoading"
        />
      </div>
    </div>
  </form>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[7.5rem] text-[13px] leading-[1.75];
  }
}
</style>
